import React, { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Filter, Search, X, HardHat, BadgeCheck, MapPin, Printer, Phone, Building2, CreditCard, CheckSquare, Square } from "lucide-react";
import SmartSearchBar from "../components/SmartSearchBar";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ExcelTools } from "./AdminDashboardPage";
import { printScreeningForm, printIdentityCard, printIdentityCardsBulk } from "../components/WorkmenPrintables";
import { contractorToWorkman } from "../lib/contractorPrintAdapter";

export default function DirectoryPage() {
  const { user, loading: authLoading, can } = useAuth();
  const isAdmin = !!user;
  const [contractors, setContractors] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smart, setSmart] = useState(null);
  const [segment, setSegment] = useState("contractor"); // "contractor" | "payroll"

  const [filters, setFilters] = useState({
    q: "",
    trade: "",
    location: "",
    site_new: "",
  });
  const [siteMasters, setSiteMasters] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [contractorsState, setContractorsState] = useState({});  // id → enriched contractor (after id-card issuance)

  useEffect(() => {
    api.get("/meta/trades").then((r) => setTrades(r.data.trades || [])).catch(() => {});
    api.get("/masters/ncc_site").then((r) => setSiteMasters(r.data || [])).catch(() => {});
  }, []);

  // Pre-fill filters from URL query params (e.g., /directory?state=Bihar from dashboard map click)
  const routerLocation = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(routerLocation.search);
    const stateQ = sp.get("state");
    const cityQ = sp.get("city");
    const segQ = sp.get("segment");
    if (segQ === "payroll" || segQ === "contractor") setSegment(segQ);
    const next = [stateQ, cityQ].filter(Boolean).join(", ");
    if (next) setFilters((f) => ({ ...f, location: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.search]);

  async function fetchList() {
    setLoading(true);
    try {
      const params = { is_payroll: segment === "payroll" };
      Object.entries(filters).forEach(([k, v]) => {
        if (k === "site_new" || v === "" || v === null || v === undefined) return;
        params[k] = v;
      });
      if (filters.site_new === "NEW") {
        params.current_status = "NEW";
      } else if (filters.site_new) {
        params.current_status = "NCCL";
        params.ncc_site_name = filters.site_new;
      }
      const r = await api.get("/contractors", { params });
      setContractors(r.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => { fetchList(); }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, filters.location, filters.site_new]);

  function applyFilters(e) {
    e?.preventDefault?.();
    setSmart(null);
    fetchList();
  }

  function clearFilters() {
    setFilters({ q: "", trade: "", location: "", site_new: "" });
    setSmart(null);
    setTimeout(fetchList, 0);
  }

  const visible = smart ? smart.matches : contractors;

  // Reset selection whenever the visible list or segment changes
  useEffect(() => { setSelectedIds(new Set()); }, [segment, contractors, smart]);

  const isPayrollSegment = segment === "payroll";
  const siteFor = (name) => (siteMasters || []).find((s) => s.name === name) || null;

  // Pick the most recent contractor record we have for an id — falls back to row
  const mergedRow = (c) => contractorsState[c.id] ? { ...c, ...contractorsState[c.id] } : c;

  async function ensureContractorIdCard(c) {
    if (c.id_card_no) return c;
    if (!c.ncc_site_name) {
      toast.error(`Set 'NCC Site' for ${c.name} before printing the Identity Card.`);
      return null;
    }
    try {
      const r = await api.post(`/contractors/${c.id}/issue-id-card`);
      const next = { ...c, id_card_no: r.data.id_card_no, id_card_issued_at: r.data.id_card_issued_at };
      setContractorsState((m) => ({ ...m, [c.id]: { id_card_no: r.data.id_card_no, id_card_issued_at: r.data.id_card_issued_at } }));
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not issue ID No.");
      return null;
    }
  }

  function handlePrintScreening(c) {
    const merged = mergedRow(c);
    printScreeningForm(contractorToWorkman(merged), merged);
  }

  async function handlePrintIdCard(c) {
    const merged = mergedRow(c);
    const enriched = await ensureContractorIdCard(merged);
    if (!enriched) return;
    printIdentityCard(contractorToWorkman(enriched), enriched, siteFor(enriched.ncc_site_name));
  }

  async function handleBulkPrintIdCards() {
    const targets = visible.filter((c) => selectedIds.has(c.id)).map(mergedRow);
    if (targets.length === 0) return;
    const enriched = [];
    for (const c of targets) {
      const e = await ensureContractorIdCard(c);
      if (e) enriched.push(e);
    }
    if (enriched.length === 0) return;
    const workmen = enriched.map(contractorToWorkman);
    printIdentityCardsBulk(workmen, null, siteMasters);
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const allSelected = isPayrollSegment && visible.length > 0 && visible.every((c) => selectedIds.has(c.id));
  function toggleAll() {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(visible.map((c) => c.id)));
  }

  if (authLoading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  // Directory exposes private contractor PII — explicit `report_directory` permission required.
  if (!can("report_directory")) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="directory-denied">
        <div className="border-2 border-slate-900 bg-white p-8 shadow-brutal">
          <BadgeCheck className="mx-auto h-10 w-10 text-slate-400" />
          <h1 className="mt-4 font-heading text-2xl font-black uppercase">Access denied</h1>
          <p className="mt-2 text-slate-600">Directory access is restricted. Ask a Super Admin to assign the <b>Report · Directory</b> permission to your role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="industrial-label text-safety-600">// Directory</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
            {segment === "payroll" ? "Payroll Manpower" : "Contractor"} Directory
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Filter by trade, location, experience and crew size.</p>
        </div>
        <div className="font-mono text-sm text-slate-600">
          <span className="industrial-label text-slate-500">Showing</span>{" "}
          <span className="font-bold text-slate-900">{visible.length}</span> record(s)
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <ExcelTools resource="contractors" segment={segment === "payroll" ? "payroll" : "contractor"} />
            <button type="button" onClick={() => window.print()} data-testid="dir-print-btn"
              className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        )}
      </div>

      {/* Segment toggle */}
      <div className="mt-6 inline-flex rounded-lg border-2 border-slate-900 bg-white p-1 shadow-brutal-sm" data-testid="dir-segment-toggle">
        <button
          type="button"
          onClick={() => setSegment("contractor")}
          data-testid="dir-segment-contractor"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-wider transition-colors ${segment === "contractor" ? "bg-ncc-navy text-white" : "text-slate-700 hover:bg-slate-100"}`}
        >
          <HardHat className="h-4 w-4" />
          CONTRACTOR
        </button>
        <button
          type="button"
          onClick={() => setSegment("payroll")}
          data-testid="dir-segment-payroll"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-wider transition-colors ${segment === "payroll" ? "bg-ncc-navy text-white" : "text-slate-700 hover:bg-slate-100"}`}
        >
          <BadgeCheck className="h-4 w-4" />
          PAYROLL MANPOWER
        </button>
      </div>

      <div className="mt-6">
        <SmartSearchBar variant="inline" onResults={setSmart} />
      </div>

      {smart && (
        <div className="mt-4 flex items-start justify-between gap-3 border-2 border-slate-900 bg-safety-50 p-4">
          <div>
            <div className="industrial-label text-safety-700">// AI Summary</div>
            <div className="mt-1 font-mono text-sm text-slate-800">{smart.summary}</div>
          </div>
          <button
            onClick={() => setSmart(null)}
            data-testid="clear-smart-btn"
            className="border-2 border-slate-900 bg-white px-3 py-1 industrial-label"
          >
            <X className="h-3 w-3 inline" /> Clear
          </button>
        </div>
      )}

      <div className="mt-8 grid gap-8 md:grid-cols-12">
        {/* Filters */}
        <aside className="md:col-span-3">
          <form onSubmit={applyFilters} className="sticky top-24 border-2 border-slate-900 bg-white p-5">
            <div className="flex items-center gap-2 border-b-2 border-slate-900 pb-3">
              <Filter className="h-4 w-4" />
              <div className="industrial-label">Filters</div>
            </div>

            <div className="mt-4 space-y-4 text-sm">
              <Field label="Search">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
                  <input
                    value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                    placeholder="Name, bio"
                    data-testid="filter-q"
                    className="w-full border-2 border-slate-300 bg-slate-50 py-2 pl-8 pr-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </div>
              </Field>

              <Field label="Trade">
                <select
                  value={filters.trade}
                  onChange={(e) => setFilters({ ...filters, trade: e.target.value })}
                  data-testid="filter-trade"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-2 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                >
                  <option value="">All trades</option>
                  {trades.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <Field label="Location">
                <input
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  placeholder="Pune, Mumbai…"
                  data-testid="filter-location"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-2 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
              </Field>

              <Field label="Site / New">
                <select
                  value={filters.site_new}
                  onChange={(e) => setFilters({ ...filters, site_new: e.target.value })}
                  data-testid="filter-site-new"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-2 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                >
                  <option value="">All (Site + New)</option>
                  <option value="NEW">NEW (not yet on a site)</option>
                  {(siteMasters || []).map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
              </Field>

              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="submit"
                  data-testid="apply-filters-btn"
                  className="border-2 border-slate-900 bg-safety-500 px-4 py-2 industrial-label text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm"
                >
                  Apply Filters
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                  className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label transition-colors hover:bg-slate-100"
                >
                  Clear
                </button>
              </div>
            </div>
          </form>
        </aside>

        {/* Results — horizontal list (rows) */}
        <main className="md:col-span-9">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-24 animate-pulse border-2 border-slate-300 bg-slate-100" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="border-2 border-dashed border-slate-400 bg-white p-10 text-center" data-testid="dir-empty">
              <div className="industrial-label text-slate-500">No {segment === "payroll" ? "payroll manpower" : "contractors"} found</div>
              <p className="mt-2 text-sm text-slate-600">Try clearing filters or widening your search.</p>
            </div>
          ) : (
            <>
              {isAdmin && isPayrollSegment && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-2 border-ncc-navy bg-ncc-tint px-3 py-2" data-testid="dir-payroll-bulk-toolbar">
                  <button
                    type="button"
                    onClick={toggleAll}
                    data-testid="dir-payroll-select-all"
                    className="inline-flex items-center gap-1.5 industrial-label text-[11px] text-ncc-navy"
                  >
                    {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                    {allSelected ? "Deselect All" : "Select All"}
                    <span className="font-mono normal-case text-slate-500">({selectedIds.size}/{visible.length})</span>
                  </button>
                  <button
                    type="button"
                    disabled={selectedIds.size === 0}
                    onClick={handleBulkPrintIdCards}
                    data-testid="dir-payroll-bulk-print-id-cards"
                    className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-ncc-gold px-3 py-1.5 industrial-label text-[11px] text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  >
                    <CreditCard className="h-3.5 w-3.5" />
                    Print ID Cards{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </button>
                </div>
              )}
              <ul className="space-y-3" data-testid="dir-list">
                {visible.map((c) => (
                  <DirectoryRow
                    key={c.id}
                    c={mergedRow(c)}
                    isAdmin={isAdmin}
                    showPayrollPrint={isAdmin && isPayrollSegment}
                    isSelected={selectedIds.has(c.id)}
                    onToggleSelect={() => toggleSelected(c.id)}
                    onPrintScreening={() => handlePrintScreening(c)}
                    onPrintIdCard={() => handlePrintIdCard(c)}
                  />
                ))}
              </ul>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function DirectoryRow({ c, isAdmin, showPayrollPrint = false, isSelected = false, onToggleSelect, onPrintScreening, onPrintIdCard }) {
  const photoUrl = c.photo?.path ? fileUrl(c.photo.path) : null;
  const initials = (c.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const trades = c.trades || [];
  // Fallback for legacy records where city/state was never split out
  let { city, state } = c;
  if (!city && !state && c.location) {
    if (c.location.includes(",")) {
      const parts = c.location.split(",").map((p) => p.trim());
      city = parts[0];
      state = parts.slice(1).join(", ");
    } else {
      city = c.location.trim();
    }
  }
  // Avoid swallowing the row-level <Link> when admin clicks the checkbox or print buttons.
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <li
      data-testid={`dir-row-${c.id}`}
      className={`group border-2 bg-white shadow-brutal-sm transition-all hover:-translate-y-0.5 hover:shadow-brutal ${isSelected ? "border-ncc-navy ring-1 ring-ncc-gold/40" : "border-slate-900"}`}
    >
      <Link to={`/contractor/${c.id}`} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-5">
        {/* Bulk-select checkbox (admin · payroll only) */}
        {showPayrollPrint && (
          <button
            type="button"
            onClick={(e) => { stop(e); onToggleSelect?.(); }}
            data-testid={`dir-payroll-select-${c.id}`}
            aria-label={isSelected ? "Deselect" : "Select"}
            className="shrink-0 text-ncc-navy hover:text-ncc-gold2"
          >
            {isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
          </button>
        )}

        {/* Avatar */}
        <div className="grid h-16 w-16 shrink-0 place-items-center border-2 border-slate-900 bg-slate-100">
          {photoUrl ? (
            <img src={photoUrl} alt={c.name} className="h-full w-full object-cover" />
          ) : (
            <span className="font-heading text-xl font-extrabold text-slate-700">{initials}</span>
          )}
        </div>

        {/* Identity + Mobile + City/State */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-lg font-extrabold tracking-tight text-slate-900">{c.name}</h3>
            {c.current_status === "NCCL" ? (
              <span className="border border-slate-900 bg-ncc-gold px-1.5 py-0.5 industrial-label">NCCL{c.ncc_site_name ? ` · ${c.ncc_site_name}` : ""}</span>
            ) : (
              <span className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label">NEW</span>
            )}
            {c.id_card_no && (
              <span className="border border-ncc-navy bg-ncc-tint px-1.5 py-0.5 industrial-label text-[10px] text-ncc-navy" data-testid={`dir-id-no-${c.id}`}>
                ID: {c.id_card_no}
              </span>
            )}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-3 sm:gap-x-4">
            <div className="flex items-center gap-1.5 font-mono text-xs text-slate-700" data-testid={`dir-row-mobile-${c.id}`}>
              <Phone className="h-3.5 w-3.5 text-ncc-navy" />
              <span className="font-bold">{c.contact_phone || "—"}</span>
            </div>
            <div className="flex items-center gap-1.5 font-mono text-xs text-slate-700" data-testid={`dir-row-city-${c.id}`}>
              <Building2 className="h-3.5 w-3.5 text-ncc-navy" />
              <span><span className="industrial-label text-slate-500">City:</span> {city || "—"}</span>
            </div>
            <div className="flex items-center gap-1.5 font-mono text-xs text-slate-700" data-testid={`dir-row-state-${c.id}`}>
              <MapPin className="h-3.5 w-3.5 text-ncc-navy" />
              <span><span className="industrial-label text-slate-500">State:</span> {state || "—"}</span>
            </div>
          </div>
        </div>

        {/* Trades chips */}
        <div className="flex flex-wrap items-center gap-1 sm:max-w-[260px]">
          {trades.slice(0, 3).map((t) => {
            const n = c.trade_counts?.[t];
            return (
              <span key={t} className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label">
                {t}{n ? ` · ${n}` : ""}
              </span>
            );
          })}
          {trades.length > 3 && (
            <span className="industrial-label text-slate-500">+{trades.length - 3}</span>
          )}
        </div>

        {/* Stats */}
        <div className="flex shrink-0 items-center gap-5 border-t border-slate-200 pt-3 sm:border-0 sm:pt-0">
          <Stat label="Crew" value={c.crew_size ?? "—"} />
          <Stat label="Exp" value={`${c.experience_years ?? 0}y`} />
          {c.is_payroll && (
            <>
              <Stat label="DOB" value={c.date_of_birth || "—"} data-testid={`dir-dob-${c.id}`} />
              <Stat label="Age" value={c.age_years || c.age_years === 0 ? `${c.age_years}y` : "—"} data-testid={`dir-age-${c.id}`} />
            </>
          )}
          <Stat label="Roster" value={c.workmen_count ?? 0} />
        </div>

        {/* Payroll print actions (admin) */}
        {showPayrollPrint && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { stop(e); onPrintScreening?.(); }}
              data-testid={`dir-payroll-print-screening-${c.id}`}
              title="Print Worker's Screening Form"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 industrial-label text-[10px] text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
            >
              <Printer className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => { stop(e); onPrintIdCard?.(); }}
              data-testid={`dir-payroll-print-id-${c.id}`}
              title="Print Identity Card"
              className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1.5 industrial-label text-[10px] text-amber-700 hover:border-amber-600 hover:bg-amber-50"
            >
              <CreditCard className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </Link>
    </li>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="industrial-label text-slate-500">{label}</div>
      <div className="mt-0.5 font-heading text-base font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="industrial-label mb-1 text-slate-600">{label}</div>
      {children}
    </label>
  );
}
